import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { errorContent } from 'App/common/Notification/Notification.config';
import { apiEndpoint, APP_REQUEST_CONFIGS, appChannel, extLnkMaintenance, trackUiAppId } from 'config/track-config';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { Shipment, ShipmentPayload } from 'models/shipments';
import { AppAsyncThunkOpts, RequestError } from 'models/Store';
import { deleteSession } from 'store/customer';
import { clearNotification, createNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import axiosInstance from 'utils/axios.utils';
import { postMxMessage, postMxNotificationMsg } from 'utils/message-exchange.utils';
import { transformShipmentResponse } from 'utils/shipment.utils';
import { AppLocation, getKnownTokenQueryFromUrl } from 'utils/window.utils';

import { setTrackAnalytics } from '../../analytics';
import { transformShipmentToAnalytics } from '../../analytics/analytics.transformer';
import { getlocationsWorkCentre } from '../../collectionPoint/collectionPoint.thunks';
import { updateCurrentArticleIndex } from '../../consigmentSwitcher/consignmentSwitcher.thunk';
import { updateDeliveryEstimateInfo } from '../../deliveryEstimate/deliveryEstimate.thunks';
import { clearShipmentList } from '../list/shipmentList.slice';

export type GetShipmentErrorData = { errorDescription?: string };
export type GetShipmentRequestError = RequestError<GetShipmentErrorData>;

export const getShipment = createAsyncThunk<Shipment, string, AppAsyncThunkOpts<GetShipmentRequestError>>(
  'shipment/getShipment',
  async (trackingId, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const knownToken = getKnownTokenQueryFromUrl().split('=')[1];
    const fetchShipmentData = async (knownToken?: string) => {
      const response = await apiGetShipment(trackingId, appChannel.detail, knownToken);
      const shipmentPayload: ShipmentPayload = response.data;
      const anonAuthType = response.headers['x-ap-auth-type'] === 'ANON';
      const isAuth = thunkAPI.getState().customer.data?.isAuthenticated;
      const sessionExpired = anonAuthType && isAuth;
      if (sessionExpired) await handleShipmentError(dispatch, trackingId, 401);

      // For mismatched email - display known flow but dont log user out
      const knownAuthType = response.headers['x-ap-auth-type'] === 'KNOWN';
      if (isAuth && knownAuthType) dispatch(deleteSession(false));

      const shipment = transformShipmentResponse(shipmentPayload);
      if (!shipment) throw new Error('invalid shipment');
      const { articles } = shipment;

      let currentArticleIndex = articles.findIndex((article) => article.articleId === trackingId);
      if (currentArticleIndex < 0) currentArticleIndex = 0;
      const currentArticle = articles[currentArticleIndex];

      const analyticsTrackData = transformShipmentToAnalytics(shipment, currentArticle);
      dispatch(setTrackAnalytics(analyticsTrackData));
      dispatch(updateDeliveryEstimateInfo(currentArticle));
      dispatch(updateCurrentArticleIndex(currentArticleIndex));
      const toWcid = currentArticle?.events ? currentArticle.events[0]?.toWcid : null;
      if (toWcid) dispatch(getlocationsWorkCentre(toWcid));

      return shipment;
    };

    try {
      return await fetchShipmentData(knownToken);
    } catch (error) {
      const authError = error as AuthError;
      if (
        (authError.isReauthRequiredError && authError.isReauthRequiredError()) ||
        (authError.isLoginRequiredError && authError.isLoginRequiredError())
      ) {
        const status = 401;
        await handleShipmentError(dispatch, trackingId, status);
        return thunkAPI.rejectWithValue({
          status,
          data: {
            errorDescription: authError.message,
          },
          isAxiosError: false,
        });
      }
      if (axios.isAxiosError(error)) {
        const err = error as AxiosError<GetShipmentErrorData>;
        const { status, data } = err.response ?? {};
        // Handle 401 when there is invalid known token - call gateway and proceed auth/anon flow
        if (status === 401 && err.response && err.response.headers['x-ap-auth-type'] === 'KNOWN') {
          return await fetchShipmentData();
        }
        await handleShipmentError(dispatch, trackingId, status, data);
        return thunkAPI.rejectWithValue({
          status,
          data,
          isAxiosError: err.isAxiosError,
        });
      }
      throw error;
    }
  }
);

/**
 * Makes an API call to get shipment from Shipments Gateway API
 * @param trackingId The tracking ID of the shipment to get
 * @param apChannelName (optional) Channel info for API analytics
 */
const apiGetShipment = (trackingId: string, apChannelName: string = appChannel.detail, token?: string) => {
  const _shipmentUrl = `${apiEndpoint.shipmentsV1}/${trackingId}`;
  const requestConfig = APP_REQUEST_CONFIGS.shipment;
  requestConfig.headers.AP_CHANNEL_NAME = apChannelName;
  // Add AP_KNOWN_FLOW and AP_AUTH_KNOWN when token is in the url
  if (token) {
    requestConfig.headers = {
      ...requestConfig.headers,
      AP_KNOWN_FLOW: 'TRUE',
      AP_AUTH_KNOWN: token,
    };
  }
  return axiosInstance.get(_shipmentUrl, requestConfig);
};

const goToListWithNotification = (
  message = errorContent.generic,
  type: string = NotificationType.ALERT,
  status: string = NotificationStatus.ERROR
) => {
  const messagePayload = { type, status, message };
  postMxNotificationMsg(messagePayload, trackUiAppId);
};

const handleShipmentError = async (
  dispatch: AppDispatch,
  trackingId: string,
  errorStatus?: number,
  error?: GetShipmentErrorData
) => {
  switch (errorStatus) {
    case 503:
      const errorDesc = error?.errorDescription;
      if (errorDesc) {
        const mxPayload = {
          heading: 'Tracking is down for maintenance',
          body: errorDesc,
        };
        postMxMessage('CONTENT_OVERRIDE', mxPayload);
      }
      const replaceHist = encodeURIComponent(AppLocation.href.replace(location.origin, ''));
      AppLocation.assign(extLnkMaintenance + '?replaceHist=' + replaceHist);
      break;
    case 400:
      const message400Payload = {
        type: NotificationType.ALERT,
        status: NotificationStatus.INFO,
        message: errorContent.invalidTrackingId([trackingId]),
      };
      postMxNotificationMsg(message400Payload, trackUiAppId);
      break;
    case 401:
      if (getKnownTokenQueryFromUrl()) return dispatch(deleteSession()); // Don't show session expiry notification if known
      dispatch(clearNotification());
      const notification: Notification = {
        type: NotificationType.ALERT,
        status: NotificationStatus.INFO,
        message: errorContent.expiredSession,
      };
      dispatch(clearShipmentList());
      dispatch(createNotification(notification));
      await dispatch(deleteSession());
      break;
    case 404:
      goToListWithNotification(errorContent.notTrackableTrackingId);
      break;
    case 500:
      goToListWithNotification(errorContent.serverError);
      break;
    default:
      goToListWithNotification();
      break;
  }
};
